import React from 'react';
import { ClientPhotoPurpose } from '@common/model/client/photo/purpose';
import { Box } from '@cp/ds/src/components/box';
import { Button } from '@cp/ds/src/components/button';
import { Card } from '@cp/ds/src/components/card';
import { Select } from '@cp/ds/src/components/select';
import { Stack } from '@cp/ds/src/components/stack';
import { Typography } from '@cp/ds/src/components/typography';
import { PromptCategory } from '@common/model/admin/thirdPartyApi';
import { Prompt } from '../api/thirdPartyApi';
import { AI_IMAGE_GENERATOR_CONSTANTS, promptsToSelectItems } from '../lib';
import { ImagePurpose, PurposeGenerationState } from '../types';
import { GenerationResultCard } from './GenerationResultCard';
import { GenerationStatusCard } from './GenerationStatusCard';

interface GenerationColumnProps {
  title: string;
  purpose: ImagePurpose;
  state: PurposeGenerationState;
  prompts: Prompt[];
  disabled?: boolean;
  isSaving?: boolean;
  onPromptChange: (key: 'smile' | 'wall' | 'clothing', value: number | undefined) => void;
  onGenerate: () => void;
  onSaveResult: (resultId: string, purpose: ClientPhotoPurpose, croppedDataUrl?: string) => void | Promise<void | boolean>;
  onResultImageChange: (resultId: string, url: string | undefined) => void;
  onDismissStatus: (statusId: string) => void;
}

const DROPDOWN_ORDER: Array<{ key: 'smile' | 'wall' | 'clothing'; category: PromptCategory; label: string }> = [
  { key: 'smile', category: PromptCategory.SMILE, label: 'Поза' },
  { key: 'wall', category: PromptCategory.WALL, label: 'Стена' },
  { key: 'clothing', category: PromptCategory.CLOTHING, label: 'Одежда' },
];

const COLUMN_SAVE_CONFIG = {
  avatar: {
    primaryPurpose: ClientPhotoPurpose.Avatar,
    secondaryPurpose: ClientPhotoPurpose.Resume,
    secondaryLabel: 'В анкету',
  },
  resume: {
    primaryPurpose: ClientPhotoPurpose.Resume,
    secondaryPurpose: ClientPhotoPurpose.Avatar,
    secondaryLabel: 'На аву',
  },
} as const;

export const GenerationColumn: React.FC<GenerationColumnProps> = ({
  title,
  purpose,
  state,
  prompts,
  disabled,
  isSaving,
  onPromptChange,
  onGenerate,
  onSaveResult,
  onResultImageChange,
  onDismissStatus,
}) => {
  const hasContent = state.results.length > 0 || state.statuses.length > 0;
  const saveConfig = COLUMN_SAVE_CONFIG[purpose];

  return (
    <Card
      sx={{
        p: { xs: 2, md: 2.5 },
        flex: { md: '1 1 0' },
        minWidth: 0,
        minHeight: 0,
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
        boxSizing: 'border-box',
        backgroundColor: AI_IMAGE_GENERATOR_CONSTANTS.COLUMN_BACKGROUND,
      }}
    >
      <Typography variant="h6">{title}</Typography>

      <Stack alignItems={{ xs: 'stretch', md: 'flex-end' }} direction={{ xs: 'column', md: 'row' }} spacing={1}>
        {DROPDOWN_ORDER.map(({ key, category, label }) => (
          <Stack key={key} spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography color="text.secondary" variant="caption">
              {label}
            </Typography>
            <Select
              itemListHeightMax={AI_IMAGE_GENERATOR_CONSTANTS.PROMPT_SELECT_LIST_HEIGHT_MAX}
              items={promptsToSelectItems(prompts, category)}
              menuFitContent
              onChange={(value) => onPromptChange(key, typeof value === 'number' ? value : Number(value))}
              placeholder="Выберите из списка"
              size="small"
              value={state.prompts[key]}
            />
          </Stack>
        ))}
        <Button
          color="pink"
          disabled={disabled}
          onClick={onGenerate}
          size="small"
          sx={{ flexShrink: 0, minWidth: 126, alignSelf: { xs: 'stretch', md: 'flex-end' } }}
          variant="primary"
        >
          Генерировать
        </Button>
      </Stack>

      {hasContent ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {state.results.map((result) => (
            <GenerationResultCard
              isSaving={isSaving}
              key={result.id}
              onImageUrlChange={(url) => onResultImageChange(result.id, url)}
              onSave={(savePurpose, cropped) => onSaveResult(result.id, savePurpose, cropped)}
              primaryPurpose={saveConfig.primaryPurpose}
              result={result}
              secondaryLabel={saveConfig.secondaryLabel}
              secondaryPurpose={saveConfig.secondaryPurpose}
            />
          ))}

          {state.statuses.map((status) => (
            <GenerationStatusCard key={status.id} onDismiss={() => onDismissStatus(status.id)} status={status} />
          ))}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }} />
      )}
    </Card>
  );
};
